import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Box,
  Checkbox,
  Combobox,
  Group,
  Loader,
  Table,
  Text,
  Tooltip,
  useCombobox,
} from '@mantine/core';
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconGripVertical,
  IconPencil,
} from '@tabler/icons-react';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useEditableGridController,
  type UseEditableGridControllerOptions,
} from './useEditableGridController';
import { useColumnResize } from './useColumnResize';
import linkifyIt from 'linkify-it';
import type {
  EditableGridCellDisplay,
  EditableGridColumn,
  EditableGridEditorState,
  EditableGridSuggestOption,
} from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const linkify = (linkifyIt as any)();

/** Разбирает текст на фрагменты: обычный текст и кликабельные URL. */
function linkifyText(text: string): React.ReactNode {
  const matches = linkify.match(text);
  if (!matches) return text;

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    result.push(
      <a
        key={match.index}
        href={match.url}
        target='_blank'
        rel='noopener noreferrer'
        style={{ color: 'var(--mantine-color-blue-6)' }}
        onClick={e => e.stopPropagation()}
      >
        {match.text}
      </a>
    );
    lastIndex = match.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}

/** Есть ли в тексте ячейки кликабельный URL (ссылка перехватывает клик по ячейке). */
function hasLink(text: string): boolean {
  return !!linkify.match(text);
}

/**
 * Контейнер display-ячейки с локальным hover-стейтом.
 *
 * Вынесен в отдельный компонент, потому что renderDisplayCell вызывается в цикле —
 * useState внутри него нарушил бы правила хуков. Когда `showEditAffordance` (ячейка
 * редактируемая и содержит ссылку, перехватывающую клик), на ховере показываем
 * иконку-карандаш «Редактировать» — гарантированную цель для входа в редактор.
 */
function DisplayCellContainer({
  canEdit,
  onStartEdit,
  showEditAffordance,
  style,
  children,
}: {
  canEdit: boolean;
  onStartEdit: () => void;
  showEditAffordance: boolean;
  style: React.CSSProperties;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      role={canEdit ? 'button' : undefined}
      tabIndex={canEdit ? 0 : undefined}
      onClick={() => {
        if (canEdit) onStartEdit();
      }}
      onKeyDown={event => {
        if (!canEdit) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onStartEdit();
        }
      }}
      onMouseEnter={showEditAffordance ? () => setHovered(true) : undefined}
      onMouseLeave={showEditAffordance ? () => setHovered(false) : undefined}
      style={style}
    >
      {children}
      {showEditAffordance && hovered && (
        <Tooltip label='Редактировать' withArrow position='left'>
          <ActionIcon
            size='xs'
            variant='subtle'
            color='gray'
            onClick={event => {
              event.stopPropagation();
              onStartEdit();
            }}
            style={{
              position: 'absolute',
              top: 1,
              right: 1,
              background: 'var(--mantine-color-body)',
            }}
          >
            <IconPencil size={12} />
          </ActionIcon>
        </Tooltip>
      )}
    </div>
  );
}

const cellTextStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '20px',
  minHeight: 20,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const plainInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 20,
  border: 0,
  outline: 'none',
  background: 'transparent',
  fontSize: 12,
  lineHeight: '20px',
  padding: 0,
  margin: 0,
  fontFamily: 'inherit',
  color: 'inherit',
  display: 'block',
  boxSizing: 'border-box',
  appearance: 'none',
};

const plainTextareaStyle: React.CSSProperties = {
  ...plainInputStyle,
  resize: 'none',
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const columnResizerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  width: 6,
  height: '100%',
  cursor: 'col-resize',
  userSelect: 'none',
  zIndex: 2,
};

const centeredIconCellStyle: React.CSSProperties = {
  paddingTop: 0,
  paddingBottom: 0,
};

const centeredIconWrapStyle: React.CSSProperties = {
  minHeight: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

type EditableGridViewActionArgs<Row> = {
  row: Row;
  rowId: number;
};

export type EditableGridExpandMode = 'single' | 'multiple';

type EditableGridExpandToggleArgs<Row> = {
  row: Row;
  rowId: number;
  expanded: boolean;
  toggle: () => void;
};

type EditableGridExpandedRowArgs<Row> = {
  row: Row;
  rowId: number;
};

export type EditableGridExpandableConfig<Row> = {
  mode?: EditableGridExpandMode;
  expandedRowIds?: number[];
  defaultExpandedRowIds?: number[];
  onExpandedRowIdsChange?: (rowIds: number[]) => void;
  columnWidth?: number;
  isRowExpandable?: (args: EditableGridExpandedRowArgs<Row>) => boolean;
  renderToggle?: (args: EditableGridExpandToggleArgs<Row>) => React.ReactNode;
  renderExpandedRow: (args: EditableGridExpandedRowArgs<Row>) => React.ReactNode;
};

type EditableGridViewSortableArgs<Row> = {
  activeRowId: number;
  overRowId: number;
  oldIndex: number;
  newIndex: number;
  rows: Row[];
};

type EditableGridViewSortableConfig<Row> = {
  columnWidth?: number;
  disabled?: boolean;
  onReorder: (args: EditableGridViewSortableArgs<Row>) => Promise<void> | void;
  onError?: (error: unknown) => void;
};

type EditableGridViewProps<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
> = {
  columns: EditableGridColumn<Row, ApiPatch, ColumnKey>[];
  rows: Row[];
  getRowId: (row: Row) => number | null | undefined;
  activeCell: { rowId: number; columnKey: ColumnKey } | null;
  activeColumn: EditableGridColumn<Row, ApiPatch, ColumnKey> | null;
  editor: EditableGridEditorState;
  suggestions: EditableGridSuggestOption[];
  loading?: boolean;
  tableMinWidth?: number;
  loadingText?: string;
  emptyText?: string;
  actionColumnWidth?: number;
  appendEmptyRow?: boolean;
  /** Если задан — ширины колонок сохраняются в localStorage. */
  persistId?: string;
  creatingRow?: boolean;
  phantomRowId?: number | null;
  justSavedCell?: { rowId: number; columnKey: ColumnKey } | null;
  expandable?: EditableGridExpandableConfig<Row>;
  sortable?: EditableGridViewSortableConfig<Row>;
  onStartCellEdit: (rowId: number | 'append', columnKey: ColumnKey) => void;
  onCancelActiveCell: () => void;
  onCommitActiveCell: (moveToNext?: boolean) => Promise<void> | void;
  onSetEditorValue: (nextValue: string) => void;
  onSelectSuggestion: (option: EditableGridSuggestOption) => void;
  onToggleCheckboxCell?: (rowId: number, columnKey: ColumnKey) => void;
  renderRowActions?: (args: EditableGridViewActionArgs<Row>) => React.ReactNode;
};

type EditableGridDataSource<
  Row,
  ApiPatch extends Record<string, unknown>,
  CreatePayload,
  ColumnKey extends string,
> = Pick<
  UseEditableGridControllerOptions<Row, ApiPatch, CreatePayload, ColumnKey>,
  'loadRows' | 'createRow' | 'updateRow' | 'deleteRow' | 'defaults' | 'mapCreatedRow' | 'onError'
>;

type EditableGridActionState = {
  loading: boolean;
  saving: boolean;
  rowSaving: boolean;
  creatingRow: boolean;
};

type EditableGridActionHelpers<Row> = {
  deleteRow: (rowId: number) => Promise<void>;
  reloadRows: () => Promise<void>;
  replaceRows: (rows: Row[]) => void;
};

export type EditableGridActionArgs<Row> = {
  row: Row;
  rowId: number;
  state: EditableGridActionState;
  actions: EditableGridActionHelpers<Row>;
};

export type EditableGridSortableArgs<Row> = EditableGridViewSortableArgs<Row> & {
  state: EditableGridActionState;
  actions: EditableGridActionHelpers<Row>;
};

export type EditableGridSortableConfig<Row> = {
  columnWidth?: number;
  disabled?: boolean;
  onReorder: (args: EditableGridSortableArgs<Row>) => Promise<void> | void;
};

export type EditableGridProps<
  Row,
  ApiPatch extends Record<string, unknown>,
  CreatePayload,
  ColumnKey extends string,
> = {
  columns: EditableGridColumn<Row, ApiPatch, ColumnKey>[];
  getRowId: (row: Row) => number | null | undefined;
  dataSource: EditableGridDataSource<Row, ApiPatch, CreatePayload, ColumnKey>;
  /** Если задан — ширины колонок сохраняются в localStorage и восстанавливаются при следующем маунте. */
  persistId?: string;
  tableMinWidth?: number;
  loadingText?: string;
  emptyText?: string;
  actionColumnWidth?: number;
  appendEmptyRow?: boolean;
  expandable?: EditableGridExpandableConfig<Row>;
  sortable?: EditableGridSortableConfig<Row>;
  renderRowActions?: (args: EditableGridActionArgs<Row>) => React.ReactNode;
};

type SortableDataRowProps = {
  rowId: number;
  disabled: boolean;
  handleCellWidth: number;
  children: React.ReactNode;
};

function SortableDataRow({ rowId, disabled, handleCellWidth, children }: SortableDataRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rowId,
    disabled,
  });

  return (
    <Table.Tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 1 } : null),
      }}
    >
      <Table.Td style={{ width: handleCellWidth }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'default' : 'grab',
          }}
          onClick={event => event.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <IconGripVertical size={14} color='var(--mantine-color-gray-6)' />
        </div>
      </Table.Td>
      {children}
    </Table.Tr>
  );
}

function isNumericColumn(column: { kind: string }): boolean {
  return column.kind === 'number' || column.kind === 'money';
}

function isEditableColumn(column: { editable?: boolean }): boolean {
  return column.editable !== false;
}

function isTextColumn(column: { kind: string }): boolean {
  return column.kind === 'text';
}

/** При фокусе выделяем весь текст — удобно при добавлении строки («Новая позиция» сразу заменяется вводом). */
function handleEditorFocus(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
  event.target.select();
}

function isMultilineTextColumn(
  column: { kind: string; multiline?: boolean | 'auto' },
  value: string
): boolean {
  if (column.kind !== 'text') return false;
  if (column.multiline === true) return true;
  if (column.multiline === false) return false;
  return value.includes('\n');
}

export function EditableGrid<
  Row,
  ApiPatch extends Record<string, unknown>,
  CreatePayload,
  ColumnKey extends string,
>({
  columns,
  getRowId,
  dataSource,
  persistId,
  tableMinWidth = 980,
  loadingText = 'Загрузка...',
  emptyText = 'Нет данных',
  actionColumnWidth = 78,
  appendEmptyRow = false,
  expandable,
  sortable,
  renderRowActions,
}: EditableGridProps<Row, ApiPatch, CreatePayload, ColumnKey>) {
  const controller = useEditableGridController({
    columns,
    getRowId,
    ...dataSource,
  });

  const { gridProps, loading, saving, isRowSaving, creatingRow, loadRows, deleteRow, replaceRows } =
    controller;

  const actionState = useMemo<EditableGridActionState>(
    () => ({
      loading,
      saving,
      rowSaving: false,
      creatingRow,
    }),
    [loading, saving, creatingRow]
  );

  const actionHelpers = useMemo<EditableGridActionHelpers<Row>>(
    () => ({
      deleteRow,
      reloadRows: loadRows,
      replaceRows,
    }),
    [deleteRow, loadRows, replaceRows]
  );

  const wrappedRowActions = useMemo(() => {
    if (!renderRowActions) return undefined;
    return ({ row, rowId }: EditableGridViewActionArgs<Row>) =>
      renderRowActions({
        row,
        rowId,
        state: {
          ...actionState,
          rowSaving: isRowSaving(rowId),
        },
        actions: actionHelpers,
      });
  }, [actionHelpers, actionState, isRowSaving, renderRowActions]);

  const wrappedSortable = useMemo<EditableGridViewSortableConfig<Row> | undefined>(() => {
    if (!sortable) return undefined;
    return {
      columnWidth: sortable.columnWidth,
      disabled: Boolean(sortable.disabled) || loading || saving || creatingRow,
      onReorder: args =>
        sortable.onReorder({
          ...args,
          state: actionState,
          actions: actionHelpers,
        }),
      onError: dataSource.onError,
    };
  }, [actionHelpers, actionState, creatingRow, dataSource.onError, loading, saving, sortable]);

  return (
    <EditableGridView
      {...gridProps}
      tableMinWidth={tableMinWidth}
      loadingText={loadingText}
      emptyText={emptyText}
      actionColumnWidth={actionColumnWidth}
      appendEmptyRow={appendEmptyRow}
      persistId={persistId}
      expandable={expandable}
      sortable={wrappedSortable}
      renderRowActions={wrappedRowActions}
    />
  );
}

export function EditableGridView<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
>({
  columns,
  rows,
  getRowId,
  activeCell,
  activeColumn,
  editor,
  suggestions,
  loading = false,
  tableMinWidth = 980,
  loadingText = 'Загрузка...',
  emptyText = 'Нет данных',
  actionColumnWidth = 78,
  appendEmptyRow = false,
  persistId,
  creatingRow = false,
  phantomRowId = null,
  justSavedCell = null,
  expandable,
  sortable,
  onStartCellEdit,
  onCancelActiveCell,
  onCommitActiveCell,
  onSetEditorValue,
  onSelectSuggestion,
  onToggleCheckboxCell,
  renderRowActions,
}: EditableGridViewProps<Row, ApiPatch, ColumnKey>) {
  const { columnWidths, getResizeHandler } = useColumnResize(columns, persistId);
  const suggestCombobox = useCombobox();
  const skipBlurRef = useRef(false);
  const editorTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [internalExpandedRowIds, setInternalExpandedRowIds] = useState<number[]>(
    () => expandable?.defaultExpandedRowIds ?? []
  );
  const sortSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const sortableRows = useMemo(
    () =>
      rows
        .map(row => ({ row, rowId: getRowId(row) }))
        .filter((item): item is { row: Row; rowId: number } => item.rowId != null),
    [getRowId, rows]
  );
  const sortableRowIds = useMemo(() => sortableRows.map(item => item.rowId), [sortableRows]);
  const showActionColumn = Boolean(renderRowActions);
  const showSortColumn = Boolean(sortable);
  const sortColumnWidth = sortable?.columnWidth ?? 30;
  const showExpandColumn = Boolean(expandable);
  const expandColumnWidth = expandable?.columnWidth ?? 34;
  const isExpandControlled = expandable?.expandedRowIds != null;
  const expandedRowIds = useMemo(() => {
    if (!expandable) return [];
    return isExpandControlled ? (expandable.expandedRowIds ?? []) : internalExpandedRowIds;
  }, [expandable, internalExpandedRowIds, isExpandControlled]);
  const expandedRowIdSet = useMemo(() => new Set(expandedRowIds), [expandedRowIds]);
  const totalColumnCount =
    columns.length +
    (showSortColumn ? 1 : 0) +
    (showActionColumn ? 1 : 0) +
    (showExpandColumn ? 1 : 0) +
    1;

  // Явная ширина таблицы в пикселях — сумма всех колонок.
  // Без этого table-layout: fixed распределяет ширину внутри 100% контейнера,
  // и при ресайзе одной колонки остальные сжимаются вместо появления скролла.
  // Явная ширина таблицы = сумма всех колонок в пикселях.
  // tableMinWidth — это ограничение вьюпорта (ScrollContainer), не самой таблицы.
  // Смешивать их нельзя: если table.width > sum(th.widths), браузер распределяет
  // остаток между колонками и нарушает ресайз.
  const totalTableWidth = useMemo(() => {
    let total = 0;
    if (showSortColumn) total += sortColumnWidth;
    if (showExpandColumn) total += expandColumnWidth;
    if (showActionColumn) total += actionColumnWidth;
    for (const column of columns) {
      total += columnWidths[column.key] ?? 0;
    }
    return total;
  }, [
    actionColumnWidth,
    columnWidths,
    columns,
    expandColumnWidth,
    showActionColumn,
    showExpandColumn,
    showSortColumn,
    sortColumnWidth,
  ]);
  const suggestDropdownVisible = useMemo(
    () => Boolean(activeColumn?.suggest) && suggestions.length > 0,
    [activeColumn?.suggest, suggestions.length]
  );
  const activeAutosizeEditor = useMemo(
    () => activeColumn != null && (isTextColumn(activeColumn) || Boolean(activeColumn.suggest)),
    [activeColumn]
  );
  const activeEditorMultiline = useMemo(() => {
    if (!activeColumn) return false;
    return isMultilineTextColumn(activeColumn, editor.value);
  }, [activeColumn, editor.value]);

  useLayoutEffect(() => {
    if (!activeAutosizeEditor) return;
    const textarea = editorTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.max(textarea.scrollHeight, 20)}px`;
  }, [activeCell, activeAutosizeEditor, editor.value]);

  const handleSortEnd = useCallback(
    (event: DragEndEvent) => {
      if (!sortable || sortable.disabled) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeRowId = Number(active.id);
      const overRowId = Number(over.id);
      if (!Number.isFinite(activeRowId) || !Number.isFinite(overRowId)) return;

      const oldIndex = sortableRowIds.findIndex(id => id === activeRowId);
      const newIndex = sortableRowIds.findIndex(id => id === overRowId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      void Promise.resolve(
        sortable.onReorder({
          activeRowId,
          overRowId,
          oldIndex,
          newIndex,
          rows,
        })
      ).catch(error => {
        if (sortable.onError) {
          sortable.onError(error);
        } else {
          console.error('EditableGrid: ошибка при сортировке строк', error);
        }
      });
    },
    [rows, sortable, sortableRowIds]
  );

  const setExpandedRowIds = useCallback(
    (nextIds: number[]) => {
      if (!expandable) return;
      if (!isExpandControlled) {
        setInternalExpandedRowIds(nextIds);
      }
      expandable.onExpandedRowIdsChange?.(nextIds);
    },
    [expandable, isExpandControlled]
  );

  const toggleExpandedRow = useCallback(
    (rowId: number) => {
      if (!expandable) return;
      const mode = expandable.mode ?? 'single';
      const isExpanded = expandedRowIdSet.has(rowId);
      if (isExpanded) {
        setExpandedRowIds(expandedRowIds.filter(id => id !== rowId));
        return;
      }

      if (mode === 'single') {
        setExpandedRowIds([rowId]);
        return;
      }

      setExpandedRowIds([...expandedRowIds, rowId]);
    },
    [expandable, expandedRowIdSet, expandedRowIds, setExpandedRowIds]
  );

  const canExpandRow = useCallback(
    (row: Row, rowId: number) => {
      return expandable?.isRowExpandable?.({ row, rowId }) ?? true;
    },
    [expandable]
  );

  useEffect(() => {
    if (suggestDropdownVisible) {
      suggestCombobox.openDropdown();
      return;
    }
    suggestCombobox.closeDropdown();
  }, [suggestDropdownVisible, suggestCombobox]);

  const runWithBlurLock = useCallback((task: () => Promise<void> | void) => {
    skipBlurRef.current = true;
    Promise.resolve(task()).finally(() => {
      setTimeout(() => {
        skipBlurRef.current = false;
      }, 0);
    });
  }, []); // skipBlurRef — стабильный ref, внешних зависимостей нет

  const handleEditorKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancelActiveCell();
        return;
      }

      if (event.key === 'Enter') {
        if (activeColumn?.suggest && suggestDropdownVisible) {
          event.preventDefault();
          if (suggestCombobox.getSelectedOptionIndex() < 0) {
            suggestCombobox.selectFirstOption();
          }
          suggestCombobox.clickSelectedOption();
          return;
        }

        if (activeEditorMultiline) {
          if (event.shiftKey || event.altKey) return;
          event.preventDefault();
          runWithBlurLock(() => onCommitActiveCell(false));
          return;
        }

        event.preventDefault();
        runWithBlurLock(() => onCommitActiveCell(false));
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        runWithBlurLock(() => onCommitActiveCell(true));
      }
    },
    [
      activeColumn,
      activeEditorMultiline,
      onCancelActiveCell,
      onCommitActiveCell,
      runWithBlurLock,
      suggestCombobox,
      suggestDropdownVisible,
    ]
  );

  const handleEditorBlur = useCallback(() => {
    if (skipBlurRef.current) return;
    void onCommitActiveCell(false);
  }, [onCommitActiveCell]);

  const renderDisplayCell = (
    rowId: number,
    column: EditableGridColumn<Row, ApiPatch, ColumnKey>,
    display: EditableGridCellDisplay
  ) => {
    const canEdit = isEditableColumn(column);
    const numeric = isNumericColumn(column);
    const isMoney = column.kind === 'money';
    const multiline = isMultilineTextColumn(column, display.text);
    const moneyMinorMatch = isMoney ? display.text.match(/([.,]\d{2})$/) : null;
    const moneyMinor = moneyMinorMatch?.[1] ?? null;
    const moneyMajor = moneyMinor ? display.text.slice(0, -moneyMinor.length) : display.text;
    const isJustSaved = justSavedCell?.rowId === rowId && justSavedCell?.columnKey === column.key;
    // Ссылка в тексте перехватывает клик по ячейке — для таких ячеек показываем
    // hover-иконку «Редактировать» как надёжную точку входа в редактор.
    const showEditAffordance = canEdit && !isMoney && hasLink(display.text);
    return (
      <DisplayCellContainer
        canEdit={canEdit}
        onStartEdit={() => onStartCellEdit(rowId, column.key)}
        showEditAffordance={showEditAffordance}
        style={{
          ...cellTextStyle,
          cursor: canEdit ? 'text' : 'default',
          justifyContent: numeric ? 'flex-end' : undefined,
          alignItems: multiline ? 'flex-start' : 'center',
          position: 'relative',
          paddingRight: display.linked ? 16 : undefined,
          background:
            'linear-gradient(90deg, color-mix(in srgb, var(--mantine-color-green-2) 50%, transparent), transparent)',
          backgroundSize: isJustSaved ? '100% 100%' : '0% 100%',
          backgroundRepeat: 'no-repeat',
          transition: 'background-size 0.25s ease',
        }}
      >
        <Text
          size='xs'
          c={display.color ?? (display.dimmed ? 'dimmed' : undefined)}
          style={{
            lineHeight: '20px',
            ...(multiline
              ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
              : {
                  display: 'block',
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
            ...(numeric ? { textAlign: 'right' as const } : null),
          }}
        >
          {isMoney && moneyMinor ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <span>{moneyMajor}</span>
              <span
                style={{
                  fontSize: 10,
                  lineHeight: '16px',
                  color: 'var(--mantine-color-gray-6)',
                }}
              >
                {moneyMinor}
              </span>
            </span>
          ) : (
            linkifyText(display.text)
          )}
        </Text>
        {display.linked && (
          <IconCheck
            size={10}
            color='var(--mantine-color-green-6)'
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              pointerEvents: 'none',
            }}
          />
        )}
      </DisplayCellContainer>
    );
  };

  const renderEditableCell = (
    rowId: number,
    column: EditableGridColumn<Row, ApiPatch, ColumnKey>,
    display: EditableGridCellDisplay
  ) => {
    if (column.kind === 'checkbox') {
      const checked = display.text === '✓';
      return (
        <div style={{ ...cellTextStyle, justifyContent: 'center' }}>
          <Checkbox
            size='xs'
            color='green'
            checked={checked}
            onChange={() => onToggleCheckboxCell?.(rowId, column.key)}
            disabled={column.editable === false}
            styles={{ input: { cursor: 'pointer' } }}
          />
        </div>
      );
    }

    if (!isEditableColumn(column)) {
      return renderDisplayCell(rowId, column, display);
    }

    const isActive = activeCell?.rowId === rowId && activeCell?.columnKey === column.key;
    if (!isActive) {
      return renderDisplayCell(rowId, column, display);
    }

    if (column.suggest) {
      return (
        <div style={{ ...cellTextStyle, alignItems: 'flex-start', position: 'relative' }}>
          <Combobox
            store={suggestCombobox}
            withinPortal
            onOptionSubmit={optionId => {
              const option = suggestions.find(item => item.id === optionId);
              if (!option) return;
              onSelectSuggestion(option);
              suggestCombobox.closeDropdown();
            }}
          >
            <Combobox.Target>
              <textarea
                ref={editorTextareaRef}
                autoFocus
                value={
                  column.placeholderValue !== undefined &&
                  editor.value === column.placeholderValue
                    ? ''
                    : editor.value
                }
                onChange={event =>
                  onSetEditorValue(event.currentTarget.value.replace(/\r?\n/g, ' '))
                }
                onFocus={handleEditorFocus}
                onKeyDown={handleEditorKeyDown}
                onBlur={handleEditorBlur}
                placeholder={column.placeholder ?? column.placeholderValue ?? ''}
                style={plainTextareaStyle}
                rows={1}
              />
            </Combobox.Target>

            {suggestDropdownVisible && (
              <Combobox.Dropdown>
                <Combobox.Options>
                  {suggestions.map(option => (
                    <Combobox.Option
                      key={option.id}
                      value={option.id}
                      onMouseDown={() => {
                        skipBlurRef.current = true;
                        setTimeout(() => {
                          skipBlurRef.current = false;
                        }, 0);
                      }}
                    >
                      <Group gap={6} wrap='nowrap'>
                        <Text
                          size='xs'
                          style={{
                            flex: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {option.label}
                        </Text>
                        {option.hint && (
                          <Text
                            size='10px'
                            c='dimmed'
                            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            {option.hint}
                          </Text>
                        )}
                      </Group>
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </Combobox.Dropdown>
            )}
          </Combobox>
        </div>
      );
    }

    const isPlaceholderValue =
      column.placeholderValue !== undefined && editor.value === column.placeholderValue;
    const displayedValue = isPlaceholderValue ? '' : editor.value;
    const placeholderText = column.placeholder ?? column.placeholderValue ?? '';

    if (isTextColumn(column)) {
      return (
        <div style={{ ...cellTextStyle, alignItems: 'flex-start' }}>
          <textarea
            ref={editorTextareaRef}
            autoFocus
            value={displayedValue}
            onChange={event => onSetEditorValue(event.currentTarget.value)}
            onFocus={handleEditorFocus}
            onKeyDown={handleEditorKeyDown}
            onBlur={handleEditorBlur}
            placeholder={placeholderText}
            style={plainTextareaStyle}
            rows={1}
          />
        </div>
      );
    }

    return (
      <div
        style={{
          ...cellTextStyle,
          justifyContent: isNumericColumn(column) ? 'flex-end' : undefined,
        }}
      >
        <input
          autoFocus
          value={displayedValue}
          onChange={event => onSetEditorValue(event.currentTarget.value)}
          onFocus={handleEditorFocus}
          onKeyDown={handleEditorKeyDown}
          onBlur={handleEditorBlur}
          inputMode={isNumericColumn(column) ? 'decimal' : 'text'}
          placeholder={placeholderText}
          style={{
            ...plainInputStyle,
            ...(isNumericColumn(column) ? { textAlign: 'right' as const } : null),
          }}
        />
      </div>
    );
  };

  const renderAppendCell = (
    column: EditableGridColumn<Row, ApiPatch, ColumnKey>,
    columnIndex: number
  ) => {
    const canEdit = isEditableColumn(column);
    if (!canEdit) {
      return <div style={cellTextStyle} />;
    }

    return (
      <div
        role='button'
        tabIndex={0}
        onClick={() => {
          if (creatingRow) return;
          onStartCellEdit('append', column.key);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (creatingRow) return;
            onStartCellEdit('append', column.key);
          }
        }}
        style={{
          ...cellTextStyle,
          cursor: creatingRow ? 'progress' : 'text',
          opacity: creatingRow ? 0.6 : 1,
        }}
      >
        {columnIndex === 0 && (
          <Text size='xs' c='dimmed' style={{ whiteSpace: 'nowrap' }}>
            Добавить строку
          </Text>
        )}
      </div>
    );
  };

  const renderExpandToggleCell = (row: Row, rowId: number) => {
    if (!expandable) return null;

    const canExpand = canExpandRow(row, rowId);
    if (!canExpand) {
      return <Table.Td style={centeredIconCellStyle} />;
    }

    const expanded = expandedRowIdSet.has(rowId);
    const toggle = () => toggleExpandedRow(rowId);

    if (expandable.renderToggle) {
      return (
        <Table.Td style={centeredIconCellStyle}>
          <div style={centeredIconWrapStyle}>
            {expandable.renderToggle({ row, rowId, expanded, toggle })}
          </div>
        </Table.Td>
      );
    }

    return (
      <Table.Td style={centeredIconCellStyle}>
        <div style={centeredIconWrapStyle}>
          <ActionIcon
            size='xs'
            variant='subtle'
            color='gray'
            aria-label={expanded ? 'Свернуть' : 'Развернуть'}
            onClick={event => {
              event.stopPropagation();
              toggle();
            }}
          >
            {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          </ActionIcon>
        </div>
      </Table.Td>
    );
  };

  if (loading) {
    return (
      <Box py='xs' px='md'>
        <Group gap='xs'>
          <Loader size='xs' />
          <Text size='xs' c='dimmed'>
            {loadingText}
          </Text>
        </Group>
      </Box>
    );
  }

  const tableBody = (
    <Table.Tbody>
      {rows.map(row => {
        const rowId = getRowId(row);
        if (rowId == null) return null;
        const expanded = expandable && canExpandRow(row, rowId) && expandedRowIdSet.has(rowId);
        const rowCells = (
          <>
            {showExpandColumn && renderExpandToggleCell(row, rowId)}
            {columns.map(column => (
              <Table.Td key={column.key}>
                {renderEditableCell(rowId, column, column.getDisplayFromRow(row))}
              </Table.Td>
            ))}
            {showActionColumn && (
              <Table.Td>{rowId !== phantomRowId && renderRowActions?.({ row, rowId })}</Table.Td>
            )}
            <Table.Td style={{ padding: 0, borderLeft: 'none' }} />
          </>
        );

        return (
          <React.Fragment key={rowId}>
            {showSortColumn ? (
              <SortableDataRow
                rowId={rowId}
                disabled={Boolean(sortable?.disabled)}
                handleCellWidth={sortColumnWidth}
              >
                {rowCells}
              </SortableDataRow>
            ) : (
              <Table.Tr>{rowCells}</Table.Tr>
            )}
            {expanded && (
              <Table.Tr
                style={{
                  verticalAlign: 'top',
                  backgroundImage: 'linear-gradient(135deg, #fdfcfb 0%, #fff7f1 100%)',
                }}
              >
                <Table.Td
                  colSpan={totalColumnCount}
                  style={{ padding: 0, borderBottomWidth: 1, overflow: 'hidden' }}
                >
                  <div style={{ maxWidth: totalTableWidth, overflow: 'hidden' }}>
                    {expandable.renderExpandedRow({ row, rowId })}
                  </div>
                </Table.Td>
              </Table.Tr>
            )}
          </React.Fragment>
        );
      })}

      {appendEmptyRow && phantomRowId == null && (
        <Table.Tr>
          {showSortColumn && <Table.Td />}
          {showExpandColumn && <Table.Td />}
          {columns.map((column, columnIndex) => (
            <Table.Td key={column.key}>{renderAppendCell(column, columnIndex)}</Table.Td>
          ))}
          {showActionColumn && <Table.Td />}
          <Table.Td style={{ padding: 0, borderLeft: 'none' }} />
        </Table.Tr>
      )}

      {appendEmptyRow && (
        <Table.Tr style={{ pointerEvents: 'none', opacity: 0.35 }}>
          {showSortColumn && <Table.Td />}
          {showExpandColumn && <Table.Td />}
          {columns.map(column => (
            <Table.Td key={column.key}>&nbsp;</Table.Td>
          ))}
          {showActionColumn && <Table.Td />}
          <Table.Td style={{ padding: 0, borderLeft: 'none' }} />
        </Table.Tr>
      )}

      {rows.length === 0 && !appendEmptyRow && (
        <Table.Tr>
          <Table.Td colSpan={totalColumnCount}>
            <Text size='xs' c='dimmed'>
              {emptyText}
            </Text>
          </Table.Td>
        </Table.Tr>
      )}
    </Table.Tbody>
  );

  const tableContent = (
    <div
      style={{
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid var(--mantine-color-default-border)',
        backgroundColor: '#fff',
      }}
    >
      <Table.ScrollContainer
        data-component-name='EditableGridScrollContainer'
        minWidth={tableMinWidth}
        style={{ '--scrollarea-scrollbar-size': '0px' } as React.CSSProperties}
      >
        <Table
          data-component-name='EditableGridTable'
          withColumnBorders
          layout='fixed'
          style={{ minWidth: totalTableWidth }}
        >
          <Table.Thead>
            <Table.Tr>
              {showSortColumn && <Table.Th style={{ width: sortColumnWidth }} />}
              {showExpandColumn && <Table.Th style={{ width: expandColumnWidth }} />}
              {columns.map(column => (
                <Table.Th
                  key={column.key}
                  style={{ width: columnWidths[column.key] ?? column.width, position: 'relative' }}
                >
                  <Text
                    size='xs'
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {column.title}
                  </Text>
                  <div onMouseDown={getResizeHandler(column.key)} style={columnResizerStyle} />
                </Table.Th>
              ))}
              {showActionColumn && <Table.Th style={{ width: actionColumnWidth }} />}
              <Table.Th style={{ width: 'auto', padding: 0, borderLeft: 'none' }} />
            </Table.Tr>
          </Table.Thead>
          {showSortColumn ? (
            <SortableContext items={sortableRowIds} strategy={verticalListSortingStrategy}>
              {tableBody}
            </SortableContext>
          ) : (
            tableBody
          )}
        </Table>
      </Table.ScrollContainer>
    </div>
  );

  if (!showSortColumn) {
    return tableContent;
  }

  return (
    <DndContext sensors={sortSensors} collisionDetection={closestCenter} onDragEnd={handleSortEnd}>
      {tableContent}
    </DndContext>
  );
}
